/**
 * The Favoriten tab, in the design's shape (COL-533).
 *
 * This used to render `PropertyCard` — the card `/rooms` lists with. The
 * design's saved card is a different object: an availability pill over the
 * photo, the room's size, the all-in price, and a Remove / Apply pair. The
 * two are not interchangeable, so this is its own card rather than more props
 * bolted onto the listing one.
 *
 * Deliberately NOT shown: the design's "4 flatmates" line. A tenant can only
 * read their own booking, so an applicant has no way to count who lives in a
 * flat — see COL-530. Rendering a number we cannot source would be worse than
 * leaving it out.
 */

import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useSavedRooms,
  useTenantApplications,
  useUnsaveRoom,
} from "@/hooks/useTenantApplications";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor, localeFor } from "@/lib/i18n/languages";
import { getImageUrl } from "@/lib/imageUrl";
import { getListingAvailability, parseDateOnly } from "@/lib/roomAvailability";
import { firstImageUrl } from "@/lib/roomImages";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";
import { formatTenantDate } from "@/lib/date-format";

export default function TenantSavedRooms() {
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const locale = localeFor(activeLanguage);
  const d = translateDictionary(
    "tenant.dashboardV2",
    getTenantDashboardV2Labels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );

  const { data: rooms = [], isPending, isError } = useSavedRooms();
  const { data: applications = [] } = useTenantApplications();
  const unsave = useUnsaveRoom();

  /** Which saved rooms this tenant has already applied to. */
  const appliedRoomIds = new Set(
    applications
      .map((a: any) => a?.room_id ?? a?.rooms?.id)
      .filter((id: unknown) => id != null)
      .map(String)
  );

  const money = (value: unknown) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);
  };

  const day = (iso: string) => {
    const parsed = parseDateOnly(iso);
    return Number.isNaN(parsed.getTime()) ? null : formatTenantDate(parsed);
  };

  if (isPending) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">{d.loading}</p>
      </Card>
    );
  }
  // Not an empty state: "you have saved nothing" and "we could not read your
  // saved rooms" are different things, and only one of them is true.
  if (isError) {
    return (
      <Card className="p-6">
        <p className="text-destructive">{d.loadFailed}</p>
      </Card>
    );
  }

  return (
    <section>
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <h2 className="text-3xl font-semibold tracking-[-0.03em] text-foreground sm:text-[2rem]">
          {d.savedTitle}
        </h2>
        <Link
          to="/properties"
          className="shrink-0 text-sm font-semibold text-foreground underline-offset-4 hover:underline"
        >
          {d.savedViewAll}
        </Link>
      </div>

      {rooms.length === 0 ? (
        <Card className="space-y-4 p-6">
          <p className="text-muted-foreground">{d.savedEmpty}</p>
          <Button asChild>
            <Link to="/properties">{d.applicationsFindRoom}</Link>
          </Button>
        </Card>
      ) : (
        <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rooms.map((room: any) => {
            const property = room?.properties;
            const raw =
              firstImageUrl(room?.images) ?? property?.image_url ?? null;
            const photo = raw ? getImageUrl(raw, { width: 720 }) : null;
            /*
              The DERIVED availability, as /rooms uses. `rooms.available` on
              its own calls a room free while its `available_from` is still in
              the future, which would contradict the same room on the listing.
            */
            const { listingAvailable, nextAvailableDate } =
              getListingAvailability(room ?? {});
            const freeFrom = nextAvailableDate ? day(nextAvailableDate) : null;
            const pill = listingAvailable
              ? d.savedAvailableNow
              : freeFrom
                ? d.savedFreeFrom.replace("{date}", freeFrom)
                : null;
            const size = Number(room?.size_sqm);
            const applied = appliedRoomIds.has(String(room?.id));

            return (
              <li key={String(room.id)}>
                <Card className="overflow-hidden rounded-[20px] p-0">
                  <div className="relative aspect-[4/3] bg-muted">
                    {photo ? (
                      <img
                        src={photo}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    ) : null}
                    {pill ? (
                      <span className="absolute left-4 top-4 rounded-full bg-background/95 px-3 py-1.5 text-[13px] font-semibold text-foreground shadow-sm">
                        {pill}
                      </span>
                    ) : null}
                  </div>

                  <div className="space-y-3 p-5">
                    <div className="min-w-0">
                      <p className="truncate text-[17px] font-semibold text-foreground">
                        {[property?.title, room?.title]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      {Number.isFinite(size) && size > 0 ? (
                        <p className="mt-1 text-sm text-muted-foreground">
                          {d.savedSqm.replace("{size}", String(size))}
                        </p>
                      ) : null}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span className="text-[17px] font-semibold text-foreground">
                        {d.savedAllIn.replace("{price}", money(room?.price))}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          disabled={unsave.isPending}
                          onClick={() => unsave.mutate(room.id)}
                        >
                          {d.savedRemove}
                        </Button>
                        {/*
                          "Applied" is a state, not a button: re-applying to a
                          room the tenant already has an open application for
                          would create a second booking row.
                        */}
                        {applied ? (
                          <span className="rounded-full bg-muted px-4 py-2 text-sm font-semibold text-muted-foreground">
                            {d.savedApplied}
                          </span>
                        ) : (
                          <Button size="sm" asChild className="rounded-full">
                            {/*
                              The ROOM id: `/property/:id` is queried against
                              `rooms.id` (see `roomListingUrl`), so passing the
                              parent property's id opens a different room or a
                              not-found page whenever the two differ.
                            */}
                            <Link to={`/property/${room?.id ?? ""}`}>
                              {d.savedApply}
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
